import Util from "@/Master/Util";


interface ItemData {
    id: string;
    value: string;
}

export default class IndexedDB {
    private tag = '[DB-IndexedDB]';
    private dbName: string = '';
    private dbVersion: number = 1;
    private db: IDBDatabase | null = null;

    constructor(dbName: string, dbVersion: number, callbacks?: (data: ItemData[]) => void) {
        this.dbName = dbName;
        this.dbVersion = dbVersion;
        this.openDatabase().then((data: IDBDatabase) => {
            if (Util.isLog) {
                console.log(this.tag, "数据库打开成功。", data);
            }
            this.getAllData().then((allData: ItemData[]) => {
                if (Util.isLog) {
                    console.log(this.tag, "获取所有数据成功", allData);
                }
                if (callbacks) callbacks(allData);
            });

        }).catch((error: any) => {
            console.error(this.tag, "数据库打开失败:", error);
        });
    }

    // 打开数据库
    private openDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            //在数据库升级时
            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = request.result;
                // 初始化数据库
                if (!db.objectStoreNames.contains("items")) {
                    db.createObjectStore("items", { keyPath: "id" });
                }

                // if (!db.objectStoreNames.contains("abs")) {
                //     db.createObjectStore("abs", { keyPath: "key" });
                // }
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(request.result);
            };

            request.onerror = () => {
                reject(new Error("Failed to open the database."));
            };
        });
    }

    // 获取事务
    private getTransaction(storeName: string, mode: IDBTransactionMode = "readwrite"): IDBTransaction {
        if (!this.db) throw new Error(this.tag + "=> Database is not open.");
        return this.db.transaction(storeName, mode);
    }

    // 添加数据
    async addData(data: ItemData): Promise<void> {
        try {
            const transaction = this.getTransaction("items");
            const store = transaction.objectStore("items");
            const request = store.add(data);

            request.onsuccess = () => {
                if (Util.isLog) console.log(this.tag, "Data added successfully.");
            };

            request.onerror = () => {
                console.error(this.tag, "Error adding data:", request.error);
            };
        } catch (error: any) {
            console.error(this.tag, "Error adding data:", error);
        }
    }

    // 获取数据
    async getData(id: string): Promise<ItemData | undefined> {
        try {
            const transaction = this.getTransaction("items", "readonly");
            const store = transaction.objectStore("items");
            const request = store.get(id);

            return new Promise((resolve, reject) => {
                request.onsuccess = () => resolve(request.result as ItemData | undefined);
                request.onerror = () => reject(new Error("Error fetching data."));
            });
        } catch (error: any) {
            console.error(this.tag, "Error fetching data:", error);
            return undefined;
        }
    }

    // 更新数据
    async updateData(data: ItemData): Promise<void> {
        try {
            const transaction = this.getTransaction("items");
            const store = transaction.objectStore("items");
            if (Util.isLog) {
                console.time(`${this.tag} putOperation`);
            }
            const request = store.put(data);
            if (Util.isLog) {
                console.timeEnd(`${this.tag} putOperation`);
                console.log(this.tag, "updateData httpRequest", request);
            }

            request.onsuccess = () => {
                if (Util.isLog) console.log(this.tag, "Data updated successfully.");
            };

            request.onerror = () => {
                console.error(this.tag, "Error updating data:", request.error);
            };
        } catch (error: any) {
            console.error(this.tag, "Error updating data:", error);
        }
    }

    // 删除数据
    async deleteData(id: string): Promise<void> {
        try {
            const transaction = this.getTransaction("items");
            const store = transaction.objectStore("items");
            const request = store.delete(id);

            request.onsuccess = () => {
                if (Util.isLog) console.log(this.tag, "Data deleted successfully.");
            };

            request.onerror = () => {
                console.error(this.tag, "Error deleting data:", request.error);
            };
        } catch (error: any) {
            console.error(this.tag, "Error deleting data:", error);
        }
    }

    // 清除所有数据
    async clearAllData(): Promise<void> {
        try {
            const transaction = this.getTransaction("items");
            const store = transaction.objectStore("items");
            const request = store.clear();

            request.onsuccess = () => {
                if (Util.isLog) console.log(this.tag, "All data cleared.");
            };

            request.onerror = () => {
                console.error(this.tag, "Error clearing data:", request.error);
            };
        } catch (error: any) {
            console.error(this.tag, "Error clearing data:", error);
        }
    }

    // 列出所有数据
    async getAllData(): Promise<ItemData[]> {
        try {
            const transaction = this.getTransaction("items", "readonly");
            const store = transaction.objectStore("items");
            const request = store.getAll();

            return new Promise((resolve: (value: ItemData[]) => void, reject) => {
                request.onsuccess = () => resolve(request.result as ItemData[]);
                request.onerror = () => reject(new Error("Error fetching all data."));
            });
        } catch (error: any) {
            console.error(this.tag, "Error fetching all data:", error);
            return [];
        }
    }


}